import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { attendanceService } from '../services/api';
import toast from 'react-hot-toast';
import * as Icons from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const DAY_NAMES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const EVENT_LABELS = { entry: 'Entrada', lunch_out: 'Sal. Almuerzo', lunch_in: 'Reg. Almuerzo', exit: 'Salida' };
const STATUS_COLORS = {
    complete: 'bg-green-100 text-green-800',
    incomplete: 'bg-yellow-100 text-yellow-800',
    absent: 'bg-red-100 text-red-800',
    day_off: 'bg-gray-100 text-gray-600',
};

const getCurrentFortnight = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const date = now.getDate();
    
    if (date <= 15) {
        return {
            startDate: new Date(year, month, 1).toISOString().slice(0, 10),
            endDate: new Date(year, month, 15).toISOString().slice(0, 10),
        };
    } else {
        const lastDay = new Date(year, month + 1, 0).getDate();
        return {
            startDate: new Date(year, month, 16).toISOString().slice(0, 10),
            endDate: new Date(year, month, lastDay).toISOString().slice(0, 10),
        };
    }
};

const generateFortnights = () => {
    const list = [];
    const now = new Date();
    let year = now.getFullYear();
    let month = now.getMonth();
    
    for (let i = 0; i < 6; i++) {
        const lastDay = new Date(year, month + 1, 0).getDate();
        const monthName = new Date(year, month).toLocaleString('es-CO', { month: 'long' });
        const capMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
        
        // Quincena 2
        list.push({
            label: `Q2 ${capMonth} ${year}`,
            startDate: new Date(year, month, 16).toISOString().slice(0, 10),
            endDate: new Date(year, month, lastDay).toISOString().slice(0, 10),
        });
        
        // Quincena 1
        list.push({
            label: `Q1 ${capMonth} ${year}`,
            startDate: new Date(year, month, 1).toISOString().slice(0, 10),
            endDate: new Date(year, month, 15).toISOString().slice(0, 10),
        });
        
        month--;
        if (month < 0) {
            month = 11;
            year--;
        }
    }
    return list;
};

const FORTNIGHTS = generateFortnights();

const AdminAttendancePage = () => {
    const [activeTab, setActiveTab] = useState('employees');
    const [employees, setEmployees] = useState([]);
    const [logs, setLogs] = useState([]);
    const [dailySummaries, setDailySummaries] = useState([]);
    const [payrollData, setPayrollData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Enrollment
    const [enrollForm, setEnrollForm] = useState({ identification: '', full_name: '', position: '' });
    const [modelsLoaded, setModelsLoaded] = useState(false);
    const [isCameraOn, setIsCameraOn] = useState(false);
    const [capturedDescriptor, setCapturedDescriptor] = useState(null);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Filters
    const [dateRange, setDateRange] = useState(getCurrentFortnight());
    const [payrollRange, setPayrollRange] = useState(getCurrentFortnight());
    const [summaryEmployeeFilter, setSummaryEmployeeFilter] = useState('');

    // Schedule editing
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [scheduleData, setScheduleData] = useState([]);
    const [showScheduleModal, setShowScheduleModal] = useState(false);

    // Employee editing
    const [editEmployee, setEditEmployee] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // Load data based on active tab
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            if (activeTab === 'employees') {
                const resp = await attendanceService.getEmployees();
                setEmployees(resp.data || []);
            } else if (activeTab === 'logs') {
                const resp = await attendanceService.getLogs(dateRange);
                setLogs(resp.data || []);
            } else if (activeTab === 'summary') {
                const params = { ...dateRange };
                if (summaryEmployeeFilter) params.employeeId = summaryEmployeeFilter;
                const resp = await attendanceService.getDailySummary(params);
                setDailySummaries(resp.data || []);
                // Also load employees list for filter if not loaded yet
                if (employees.length === 0) {
                    try {
                        const empResp = await attendanceService.getEmployees();
                        setEmployees(empResp.data || []);
                    } catch (_) {}
                }
            } else if (activeTab === 'payroll') {
                const resp = await attendanceService.getPayrollReport(payrollRange);
                setPayrollData(resp.data || []);
            }
        } catch (err) {
            console.error(err);
            toast.error('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, [activeTab, dateRange, payrollRange, summaryEmployeeFilter]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // ==================== ENROLLMENT ====================
    const loadModels = async () => {
        try {
            await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
            await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
            await faceapi.nets.faceRecognitionNet.loadFromUri('/models');
            setModelsLoaded(true);
            toast.success('Modelos cargados');
        } catch (err) {
            toast.error('Error cargando modelos faciales');
        }
    };

    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: {} });
            videoRef.current.srcObject = stream;
            streamRef.current = stream;
            setIsCameraOn(true);
        } catch (err) {
            toast.error('Error accediendo a la cámara');
        }
    };

    const stopCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            setIsCameraOn(false);
        }
    };

    const captureFace = async () => {
        if (!videoRef.current) return;
        const detection = await faceapi
            .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
            .withFaceLandmarks()
            .withFaceDescriptor();
        if (detection) {
            setCapturedDescriptor(Array.from(detection.descriptor));
            toast.success('Rostro capturado');
        } else {
            toast.error('No se detectó rostro');
        }
    };

    const handleEnroll = async () => {
        if (!enrollForm.identification || !enrollForm.full_name || !capturedDescriptor) {
            toast.error('Complete todos los campos y capture el rostro');
            return;
        }
        try {
            await attendanceService.enrollEmployee({
                ...enrollForm,
                face_descriptors: [capturedDescriptor],
            });
            toast.success('Empleado registrado');
            setEnrollForm({ identification: '', full_name: '', position: '' });
            setCapturedDescriptor(null);
            stopCamera();
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Error al registrar');
        }
    };

    // ==================== SCHEDULE ====================
    const openScheduleModal = async (employee) => {
        setSelectedEmployee(employee);
        try {
            const resp = await attendanceService.getSchedule(employee.id);
            const existing = resp.data || [];
            // Fill all 7 days
            const fullSchedule = Array.from({ length: 7 }, (_, i) => {
                const found = existing.find(s => s.day_of_week === i);
                return found || {
                    day_of_week: i,
                    start_time: '08:00',
                    lunch_out_time: '12:00',
                    lunch_in_time: '13:00',
                    end_time: '17:00',
                    is_day_off: i === 0 ? true : false,
                };
            });
            setScheduleData(fullSchedule);
            setShowScheduleModal(true);
        } catch (err) {
            toast.error('Error cargando horario');
        }
    };

    const saveSchedule = async () => {
        try {
            await attendanceService.setSchedule(selectedEmployee.id, scheduleData);
            toast.success('Horario guardado');
            setShowScheduleModal(false);
        } catch (err) {
            toast.error('Error guardando horario');
        }
    };

    const updateScheduleDay = (dayIndex, field, value) => {
        setScheduleData(prev => prev.map((d, i) => i === dayIndex ? { ...d, [field]: value } : d));
    };

    // ==================== EMPLOYEE EDIT ====================
    const openEditModal = (emp) => {
        setEditEmployee({ ...emp });
        setShowEditModal(true);
    };

    const saveEmployee = async () => {
        try {
            await attendanceService.updateEmployee(editEmployee.id, {
                position: editEmployee.position,
                salary_base: parseFloat(editEmployee.salary_base) || 0,
                commission_pct: parseFloat(editEmployee.commission_pct) || 0,
                transport_allowance: parseFloat(editEmployee.transport_allowance) || 0,
                hourly_rate: parseFloat(editEmployee.hourly_rate) || 0,
            });
            toast.success('Empleado actualizado');
            setShowEditModal(false);
            fetchData();
        } catch (err) {
            toast.error('Error actualizando empleado');
        }
    };

    const fmtCurrency = (val) => {
        if (!val || val === 0) return '—';
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(val);
    };

    // ==================== EXPORT ====================
    const handleExportExcel = async (range) => {
        try {
            await attendanceService.exportPayrollExcel(range);
            toast.success('Excel descargado');
        } catch (err) {
            toast.error('Error exportando');
        }
    };

    // ==================== TABS ====================
    const tabs = [
        { key: 'employees', label: 'Empleados', icon: Icons.Users },
        { key: 'logs', label: 'Registros', icon: Icons.ClipboardList },
        { key: 'summary', label: 'Resumen Diario', icon: Icons.Calendar },
        { key: 'payroll', label: 'Nómina', icon: Icons.FileSpreadsheet },
        { key: 'enroll', label: 'Enrolar', icon: Icons.UserPlus },
    ];

    const formatTime = (dt) => {
        if (!dt) return '—';
        return new Date(dt).toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });
    };

    const formatMins = (mins) => {
        if (!mins || mins === 0) return '—';
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return h > 0 ? `${h}h ${m}m` : `${m}m`;
    };

    const safeFormat = (dateStr, fmt) => {
        if (!dateStr) return '—';
        try {
            const d = new Date(dateStr);
            if (isNaN(d.getTime())) return '—';
            return format(d, fmt, { locale: es });
        } catch { return '—'; }
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Icons.ClipboardCheck className="w-7 h-7 text-blue-600" />
                    Panel de Asistencia
                </h1>
                <button onClick={fetchData} className="btn btn-secondary text-sm" disabled={loading}>
                    <Icons.RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                    Actualizar
                </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
                {tabs.map(t => (
                    <button
                        key={t.key}
                        onClick={() => setActiveTab(t.key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.key ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
                    >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ==================== EMPLOYEES TAB ==================== */}
            {activeTab === 'employees' && (
                <div className="card">
                    <div className="card-content p-0 overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b-2 border-gray-200 bg-gray-50">
                                    <th className="text-left py-2 px-3 font-semibold">Nombre</th>
                                    <th className="text-left py-2 px-2 font-semibold">Cédula</th>
                                    <th className="text-left py-2 px-2 font-semibold">Cargo</th>
                                    <th className="text-right py-2 px-2 font-semibold text-green-700">Salario Base</th>
                                    <th className="text-center py-2 px-2 font-semibold text-blue-700">% Comisión</th>
                                    <th className="text-right py-2 px-2 font-semibold text-purple-700">Aux. Transporte</th>
                                    <th className="text-center py-2 px-2 font-semibold">Estado</th>
                                    <th className="text-center py-2 px-2 font-semibold">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {employees.map(emp => (
                                    <tr key={emp.id} className="border-b border-gray-100 hover:bg-gray-50">
                                        <td className="py-2 px-3 font-medium">{emp.full_name}</td>
                                        <td className="py-2 px-2 text-gray-600 text-xs">{emp.identification}</td>
                                        <td className="py-2 px-2 text-gray-600">{emp.position || '—'}</td>
                                        <td className="py-2 px-2 text-right font-medium text-green-700">{fmtCurrency(emp.salary_base)}</td>
                                        <td className="py-2 px-2 text-center">{emp.commission_pct > 0 ? `${emp.commission_pct}%` : '—'}</td>
                                        <td className="py-2 px-2 text-right text-purple-700">{fmtCurrency(emp.transport_allowance)}</td>
                                        <td className="py-2 px-2 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                                {emp.is_active ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <div className="flex items-center gap-2 justify-center">
                                                <button onClick={() => openEditModal(emp)} className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-0.5" title="Editar">
                                                    <Icons.Pencil className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => openScheduleModal(emp)} className="text-gray-500 hover:text-gray-700 text-xs flex items-center gap-0.5" title="Horario">
                                                    <Icons.Clock className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {employees.length === 0 && (
                                    <tr><td colSpan={8} className="py-8 text-center text-gray-400">No hay empleados registrados</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ==================== LOGS TAB ==================== */}
            {activeTab === 'logs' && (
                <div>
                    <div className="flex gap-3 mb-4 items-center">
                        <input type="date" value={dateRange.startDate} onChange={e => setDateRange(p => ({ ...p, startDate: e.target.value }))} className="form-input text-sm py-1.5" />
                        <span className="text-gray-400">a</span>
                        <input type="date" value={dateRange.endDate} onChange={e => setDateRange(p => ({ ...p, endDate: e.target.value }))} className="form-input text-sm py-1.5" />
                    </div>
                    <div className="card">
                        <div className="card-content p-4">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-200">
                                        <th className="text-left py-2 px-3">Empleado</th>
                                        <th className="text-left py-2 px-3">Cédula</th>
                                        <th className="text-center py-2 px-3">Tipo</th>
                                        <th className="text-center py-2 px-3">Hora</th>
                                        <th className="text-center py-2 px-3">Foto</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(log => (
                                        <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-2 px-3 font-medium">{log.full_name}</td>
                                            <td className="py-2 px-3 text-gray-600">{log.identification}</td>
                                            <td className="py-2 px-3 text-center">
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                                    {EVENT_LABELS[log.type] || log.type}
                                                </span>
                                            </td>
                                            <td className="py-2 px-3 text-center text-gray-700">
                                                {safeFormat(log.timestamp, 'dd/MM/yyyy HH:mm')}
                                            </td>
                                            <td className="py-2 px-3 text-center">
                                                {log.photo_path && (
                                                    <img src={`/api/${log.photo_path}`} alt="audit" className="w-10 h-10 rounded object-cover mx-auto" />
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    {logs.length === 0 && (
                                        <tr><td colSpan={5} className="py-8 text-center text-gray-400">No hay registros</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== DAILY SUMMARY TAB ==================== */}
            {activeTab === 'summary' && (() => {
                // Build rows with weekly subtotals
                const buildRowsWithWeekSubtotals = () => {
                    if (!dailySummaries.length) return [];
                    const rows = [];
                    let currentWeekMins = 0;
                    let currentWeekStart = null;
                    let prevISOWeek = null;

                    const getISOWeek = (dateStr) => {
                        const d = new Date(dateStr + 'T12:00:00');
                        const day = d.getDay() || 7;
                        d.setDate(d.getDate() + 4 - day);
                        const yearStart = new Date(d.getFullYear(), 0, 1);
                        return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
                    };

                    // Sort by employee then date ASC for subtotals
                    const sorted = [...dailySummaries].sort((a, b) => {
                        if (a.full_name < b.full_name) return -1;
                        if (a.full_name > b.full_name) return 1;
                        return (a.date || '').localeCompare(b.date || '');
                    });

                    let currentEmployee = null;
                    let empTotalMins = 0;

                    sorted.forEach((s, idx) => {
                        const dateStr = typeof s.date === 'string' ? s.date.slice(0, 10) : '';
                        const week = getISOWeek(dateStr);
                        const isNewEmployee = s.full_name !== currentEmployee;

                        // Employee changed — flush week + employee totals
                        if (isNewEmployee && currentEmployee !== null) {
                            if (currentWeekMins > 0) {
                                rows.push({ type: 'week_subtotal', mins: currentWeekMins, weekStart: currentWeekStart, employee: currentEmployee });
                            }
                            rows.push({ type: 'employee_total', mins: empTotalMins, employee: currentEmployee });
                            currentWeekMins = 0;
                            empTotalMins = 0;
                            prevISOWeek = null;
                        }

                        // Week changed within same employee
                        if (!isNewEmployee && prevISOWeek !== null && week !== prevISOWeek && currentWeekMins > 0) {
                            rows.push({ type: 'week_subtotal', mins: currentWeekMins, weekStart: currentWeekStart, employee: currentEmployee });
                            currentWeekMins = 0;
                        }

                        if (isNewEmployee || prevISOWeek === null || week !== prevISOWeek) {
                            currentWeekStart = dateStr;
                        }

                        currentEmployee = s.full_name;
                        prevISOWeek = week;
                        const mins = parseInt(s.total_worked_minutes) || 0;
                        currentWeekMins += mins;
                        empTotalMins += mins;

                        rows.push({ type: 'data', data: s });
                    });

                    // Flush last week + employee
                    if (currentWeekMins > 0) {
                        rows.push({ type: 'week_subtotal', mins: currentWeekMins, weekStart: currentWeekStart, employee: currentEmployee });
                    }
                    if (currentEmployee && empTotalMins > 0) {
                        rows.push({ type: 'employee_total', mins: empTotalMins, employee: currentEmployee });
                    }

                    return rows;
                };

                const tableRows = buildRowsWithWeekSubtotals();

                return (
                <div>
                    {/* Filters bar */}
                    <div className="flex gap-3 mb-4 items-center flex-wrap">
                        <select
                            className="form-input text-sm py-1.5 px-3 min-w-[220px] bg-white border border-gray-300 rounded-md font-medium text-gray-700"
                            onChange={(e) => {
                                const [start, end] = e.target.value.split('|');
                                setDateRange({ startDate: start, endDate: end });
                            }}
                            value={`${dateRange.startDate}|${dateRange.endDate}`}
                        >
                            <option value="" disabled>Seleccionar quincena...</option>
                            {FORTNIGHTS.map((f, i) => (
                                <option key={i} value={`${f.startDate}|${f.endDate}`}>
                                    {f.label} ({f.startDate.slice(8,10)}/{f.startDate.slice(5,7)} al {f.endDate.slice(8,10)}/{f.endDate.slice(5,7)})
                                </option>
                            ))}
                        </select>
                        <span className="text-gray-400 text-xs">ó</span>
                        <input type="date" value={dateRange.startDate} onChange={e => setDateRange(p => ({ ...p, startDate: e.target.value }))} className="form-input text-sm py-1.5" />
                        <span className="text-gray-400">a</span>
                        <input type="date" value={dateRange.endDate} onChange={e => setDateRange(p => ({ ...p, endDate: e.target.value }))} className="form-input text-sm py-1.5" />
                        <select
                            className="form-input text-sm py-1.5 px-3 min-w-[180px] bg-white border border-gray-300 rounded-md text-gray-700"
                            value={summaryEmployeeFilter}
                            onChange={(e) => setSummaryEmployeeFilter(e.target.value)}
                        >
                            <option value="">Todos los empleados</option>
                            {employees.filter(e => e.is_active).map(emp => (
                                <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                            ))}
                        </select>
                        <button onClick={() => handleExportExcel(dateRange)} className="btn btn-primary text-sm ml-auto">
                            <Icons.Download className="w-4 h-4 mr-1" />
                            Exportar Excel
                        </button>
                    </div>
                    <div className="card">
                        <div className="card-content p-0 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-200 bg-gray-50">
                                        <th className="text-left py-2.5 px-3 font-semibold">Empleado</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Fecha</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Día</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-green-700">Entrada</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-orange-600">Sal. Alm.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-orange-600">Reg. Alm.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-red-600">Salida</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Retardo</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">H. Extra</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Total Día</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableRows.map((row, idx) => {
                                        if (row.type === 'week_subtotal') {
                                            const hrs = Math.floor(row.mins / 60);
                                            const mins = row.mins % 60;
                                            return (
                                                <tr key={`ws-${idx}`} className="bg-blue-50 border-b border-blue-200">
                                                    <td colSpan={9} className="py-1.5 px-3 text-right text-xs font-bold text-blue-700 uppercase">
                                                        Subtotal Semana
                                                    </td>
                                                    <td className="py-1.5 px-2 text-center font-bold text-blue-800 text-sm">{hrs}h {mins > 0 ? `${mins}m` : ''}</td>
                                                    <td></td>
                                                </tr>
                                            );
                                        }
                                        if (row.type === 'employee_total') {
                                            const hrs = Math.floor(row.mins / 60);
                                            const mins = row.mins % 60;
                                            return (
                                                <tr key={`et-${idx}`} className="bg-purple-50 border-b-2 border-purple-300">
                                                    <td colSpan={9} className="py-2 px-3 text-right text-sm font-black text-purple-700 uppercase">
                                                        Total {row.employee}
                                                    </td>
                                                    <td className="py-2 px-2 text-center font-black text-purple-900 text-base">{hrs}h {mins > 0 ? `${mins}m` : ''}</td>
                                                    <td></td>
                                                </tr>
                                            );
                                        }
                                        const s = row.data;
                                        const dateStr = typeof s.date === 'string' ? s.date.slice(0, 10) : '';
                                        const dayName = dateStr ? DAY_NAMES[new Date(dateStr + 'T12:00:00').getDay()] : '';
                                        return (
                                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-2 px-3 font-medium">{s.full_name}</td>
                                            <td className="py-2 px-2 text-center text-gray-600 text-xs">{dateStr ? safeFormat(dateStr + 'T12:00:00', 'dd MMM yyyy') : '—'}</td>
                                            <td className="py-2 px-2 text-center text-gray-500 text-xs">{dayName.slice(0,3)}</td>
                                            <td className="py-2 px-2 text-center">{formatTime(s.entry_time)}</td>
                                            <td className="py-2 px-2 text-center">{formatTime(s.lunch_out_time)}</td>
                                            <td className="py-2 px-2 text-center">{formatTime(s.lunch_in_time)}</td>
                                            <td className="py-2 px-2 text-center">{formatTime(s.exit_time)}</td>
                                            <td className="py-2 px-2 text-center">
                                                {s.tardiness_minutes > 0 ? (
                                                    <span className="text-red-600 font-medium">{formatMins(s.tardiness_minutes)}</span>
                                                ) : '—'}
                                            </td>
                                            <td className="py-2 px-2 text-center">
                                                {s.overtime_minutes > 0 ? (
                                                    <span className="text-blue-600 font-medium">{formatMins(s.overtime_minutes)}</span>
                                                ) : '—'}
                                            </td>
                                            <td className="py-2 px-2 text-center font-medium">{formatMins(s.total_worked_minutes)}</td>
                                            <td className="py-2 px-2 text-center">
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s.status] || 'bg-gray-100'}`}>
                                                    {{ complete: 'Completo', incomplete: 'Incompleto', absent: 'Ausente', day_off: 'Descanso' }[s.status] || s.status}
                                                </span>
                                            </td>
                                        </tr>
                                        );
                                    })}
                                    {dailySummaries.length === 0 && (
                                        <tr><td colSpan={11} className="py-8 text-center text-gray-400">No hay datos para el rango seleccionado</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                );
            })()}

            {/* ==================== PAYROLL TAB ==================== */}
            {activeTab === 'payroll' && (
                <div>
                    <div className="flex gap-3 mb-4 items-center flex-wrap">
                        <select 
                            className="form-input text-sm py-1.5 px-3 min-w-[220px] bg-white border border-gray-300 rounded-md font-medium text-gray-700 focus:ring-blue-500 focus:border-blue-500"
                            onChange={(e) => {
                                const [start, end] = e.target.value.split('|');
                                setPayrollRange({ startDate: start, endDate: end });
                            }}
                            value={`${payrollRange.startDate}|${payrollRange.endDate}`}
                        >
                            {FORTNIGHTS.map((f, i) => (
                                <option key={i} value={`${f.startDate}|${f.endDate}`}>
                                    {f.label} ({f.startDate.slice(8,10)}/{f.startDate.slice(5,7)} al {f.endDate.slice(8,10)}/{f.endDate.slice(5,7)})
                                </option>
                            ))}
                        </select>
                        <button onClick={() => handleExportExcel(payrollRange)} className="btn btn-primary text-sm ml-auto">
                            <Icons.Download className="w-4 h-4 mr-1" />
                            Exportar Excel
                        </button>
                    </div>
                    <div className="card">
                        <div className="card-content p-0 overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b-2 border-gray-200 bg-gray-50">
                                        <th className="text-left py-2.5 px-3 font-semibold">Empleado</th>
                                        <th className="text-left py-2.5 px-2 font-semibold">Cargo</th>
                                        <th className="text-right py-2.5 px-2 font-semibold text-green-700">Salario</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Días Trab.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold">Horas Trab.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-red-600">Retardo</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-gray-700">Ord. Diurno</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-blue-800">Rec. Noct.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-blue-600">Ex. Diurna</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-indigo-700">Ex. Nocturna</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-red-500">Dom. Diurno</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-red-800">Dom. Noct.</th>
                                        <th className="text-right py-2.5 px-2 font-semibold text-purple-700">Aux. Transp.</th>
                                        <th className="text-center py-2.5 px-2 font-semibold text-orange-600">% Comisión</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payrollData.map((r, idx) => (
                                        <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                                            <td className="py-2.5 px-3 font-medium">{r.full_name}</td>
                                            <td className="py-2.5 px-2 text-gray-600">{r.position || '—'}</td>
                                            <td className="py-2.5 px-2 text-right font-medium text-green-700">{fmtCurrency(r.salary_base)}</td>
                                            <td className="py-2.5 px-2 text-center font-medium">{r.days_worked || 0}</td>
                                            <td className="py-2.5 px-2 text-center font-bold text-gray-900">{r.total_worked_hours || 0}</td>
                                            <td className="py-2.5 px-2 text-center">
                                                {r.total_tardiness_minutes > 0 ? (
                                                    <span className="text-red-600 font-medium">{formatMins(r.total_tardiness_minutes)}</span>
                                                ) : '—'}
                                            </td>
                                            <td className="py-2.5 px-2 text-center text-gray-700 font-medium">{r.regular_diurnal > 0 ? r.regular_diurnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-center text-blue-800 font-medium">{r.regular_nocturnal > 0 ? r.regular_nocturnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-center text-blue-600 font-medium">{r.overtime_diurnal > 0 ? r.overtime_diurnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-center text-indigo-700 font-medium">{r.overtime_nocturnal > 0 ? r.overtime_nocturnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-center text-red-500 font-medium">{r.holiday_diurnal > 0 ? r.holiday_diurnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-center text-red-800 font-medium">{r.holiday_nocturnal > 0 ? r.holiday_nocturnal + ' h' : '—'}</td>
                                            <td className="py-2.5 px-2 text-right text-purple-700">{fmtCurrency(r.transport_allowance)}</td>
                                            <td className="py-2.5 px-2 text-center">{r.commission_pct > 0 ? `${r.commission_pct}%` : '—'}</td>
                                        </tr>
                                    ))}
                                    {payrollData.length === 0 && (
                                        <tr><td colSpan={9} className="py-8 text-center text-gray-400">No hay datos para el rango seleccionado</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== ENROLL TAB ==================== */}
            {activeTab === 'enroll' && (
                <div className="max-w-2xl">
                    <div className="card">
                        <div className="card-content p-6">
                            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                                <Icons.UserPlus className="w-5 h-5 text-blue-600" />
                                Enrolar Nuevo Empleado
                            </h3>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cédula</label>
                                    <input
                                        type="text"
                                        value={enrollForm.identification}
                                        onChange={e => setEnrollForm(p => ({ ...p, identification: e.target.value }))}
                                        className="form-input w-full"
                                        placeholder="Número de cédula"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                                    <input
                                        type="text"
                                        value={enrollForm.full_name}
                                        onChange={e => setEnrollForm(p => ({ ...p, full_name: e.target.value }))}
                                        className="form-input w-full"
                                        placeholder="Nombre completo"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Cargo</label>
                                    <input
                                        type="text"
                                        value={enrollForm.position}
                                        onChange={e => setEnrollForm(p => ({ ...p, position: e.target.value }))}
                                        className="form-input w-full"
                                        placeholder="Ej: Empacador, Mensajero"
                                    />
                                </div>
                            </div>

                            {/* Camera */}
                            <div className="mb-4">
                                <div className="flex gap-2 mb-3">
                                    {!modelsLoaded && (
                                        <button onClick={loadModels} className="btn btn-secondary text-sm">
                                            <Icons.Cpu className="w-4 h-4 mr-1" /> Cargar Modelos
                                        </button>
                                    )}
                                    {modelsLoaded && !isCameraOn && (
                                        <button onClick={startCamera} className="btn btn-secondary text-sm">
                                            <Icons.Camera className="w-4 h-4 mr-1" /> Activar Cámara
                                        </button>
                                    )}
                                    {isCameraOn && (
                                        <>
                                            <button onClick={captureFace} className="btn btn-primary text-sm">
                                                <Icons.Scan className="w-4 h-4 mr-1" /> Capturar Rostro
                                            </button>
                                            <button onClick={stopCamera} className="btn btn-secondary text-sm">Detener</button>
                                        </>
                                    )}
                                </div>
                                <video ref={videoRef} autoPlay muted className={`w-full max-w-md rounded-lg bg-black ${!isCameraOn ? 'hidden' : ''}`} />
                                {capturedDescriptor && (
                                    <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                                        <Icons.CheckCircle className="w-4 h-4" />
                                        Rostro capturado correctamente
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={handleEnroll}
                                disabled={!enrollForm.identification || !enrollForm.full_name || !capturedDescriptor}
                                className="btn btn-primary"
                            >
                                <Icons.Save className="w-4 h-4 mr-2" />
                                Registrar Empleado
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== SCHEDULE MODAL ==================== */}
            {showScheduleModal && selectedEmployee && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowScheduleModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Icons.Clock className="w-5 h-5 text-blue-600" />
                                Horario — {selectedEmployee.full_name}
                            </h3>
                            <button onClick={() => setShowScheduleModal(false)} className="text-gray-400 hover:text-gray-600">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>

                        <table className="w-full text-sm mb-4">
                            <thead>
                                <tr className="border-b-2 border-gray-200">
                                    <th className="text-left py-2 px-2">Día</th>
                                    <th className="text-center py-2 px-2">Entrada</th>
                                    <th className="text-center py-2 px-2">Sal. Alm.</th>
                                    <th className="text-center py-2 px-2">Reg. Alm.</th>
                                    <th className="text-center py-2 px-2">Salida</th>
                                    <th className="text-center py-2 px-2">Descanso</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduleData.map((day, idx) => (
                                    <tr key={idx} className={`border-b border-gray-100 ${day.is_day_off ? 'bg-gray-50 opacity-60' : ''}`}>
                                        <td className="py-2 px-2 font-medium">{DAY_NAMES[day.day_of_week]}</td>
                                        <td className="py-2 px-2 text-center">
                                            <input type="time" value={day.start_time?.slice(0, 5)} onChange={e => updateScheduleDay(idx, 'start_time', e.target.value)} disabled={day.is_day_off} className="form-input text-xs py-1 w-24 text-center" />
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <input type="time" value={day.lunch_out_time?.slice(0, 5)} onChange={e => updateScheduleDay(idx, 'lunch_out_time', e.target.value)} disabled={day.is_day_off} className="form-input text-xs py-1 w-24 text-center" />
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <input type="time" value={day.lunch_in_time?.slice(0, 5)} onChange={e => updateScheduleDay(idx, 'lunch_in_time', e.target.value)} disabled={day.is_day_off} className="form-input text-xs py-1 w-24 text-center" />
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <input type="time" value={day.end_time?.slice(0, 5)} onChange={e => updateScheduleDay(idx, 'end_time', e.target.value)} disabled={day.is_day_off} className="form-input text-xs py-1 w-24 text-center" />
                                        </td>
                                        <td className="py-2 px-2 text-center">
                                            <input type="checkbox" checked={!!day.is_day_off} onChange={e => updateScheduleDay(idx, 'is_day_off', e.target.checked)} className="rounded" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowScheduleModal(false)} className="btn btn-secondary">Cancelar</button>
                            <button onClick={saveSchedule} className="btn btn-primary">
                                <Icons.Save className="w-4 h-4 mr-1" />
                                Guardar Horario
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==================== EDIT EMPLOYEE MODAL ==================== */}
            {showEditModal && editEmployee && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowEditModal(false)}>
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold flex items-center gap-2">
                                <Icons.Pencil className="w-5 h-5 text-blue-600" />
                                Editar — {editEmployee.full_name}
                            </h3>
                            <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-gray-600">
                                <Icons.X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Cargo</label>
                                <input type="text" value={editEmployee.position || ''} onChange={e => setEditEmployee(p => ({ ...p, position: e.target.value }))} className="form-input w-full text-sm" placeholder="Ej: Empacador" />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Salario Base Mensual ($)</label>
                                <input type="number" value={editEmployee.salary_base || ''} onChange={e => setEditEmployee(p => ({ ...p, salary_base: e.target.value }))} className="form-input w-full text-sm" placeholder="0" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">% Comisión</label>
                                    <input type="number" step="0.5" value={editEmployee.commission_pct || ''} onChange={e => setEditEmployee(p => ({ ...p, commission_pct: e.target.value }))} className="form-input w-full text-sm" placeholder="0" />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-500 mb-1">Aux. Transporte ($)</label>
                                    <input type="number" value={editEmployee.transport_allowance || ''} onChange={e => setEditEmployee(p => ({ ...p, transport_allowance: e.target.value }))} className="form-input w-full text-sm" placeholder="0" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-500 mb-1">Valor Hora ($)</label>
                                <input type="number" value={editEmployee.hourly_rate || ''} onChange={e => setEditEmployee(p => ({ ...p, hourly_rate: e.target.value }))} className="form-input w-full text-sm" placeholder="0" />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-5">
                            <button onClick={() => setShowEditModal(false)} className="btn btn-secondary">Cancelar</button>
                            <button onClick={saveEmployee} className="btn btn-primary">
                                <Icons.Save className="w-4 h-4 mr-1" />
                                Guardar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminAttendancePage;
